import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft, Download, Users, BarChart3, ListChecks } from 'lucide-react';
import {
  getSurveyResults, getSurveyResponses, exportSurveyCsv,
} from '../../../services/api/survey.service';
import type {
  SurveyResults as Results, SurveyQuestionResult, SurveyResponsesPage,
} from '../../../types/survey';

interface Props {
  surveyId: number;
  onBack: () => void;
}

const PAGE_SIZE = 25;

const ChoiceResult: React.FC<{ q: SurveyQuestionResult }> = ({ q }) => {
  const options = [...(q.options || [])].sort((a, b) => b.count - a.count);
  return (
    <div className="space-y-2">
      {options.map((o) => (
        <div key={o.key}>
          <div className="flex items-center justify-between text-[0.72rem] mb-1">
            <span className="text-gray-700">{o.label}</span>
            <span className="text-gray-500 tabular-nums">{o.count} · {o.percent}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-black rounded-full" style={{ width: `${o.percent}%` }} />
          </div>
        </div>
      ))}
      {options.length === 0 && <p className="text-[0.72rem] text-gray-400">No options.</p>}
    </div>
  );
};

const ScaleResult: React.FC<{ q: SurveyQuestionResult }> = ({ q }) => (
  <div className="flex flex-wrap gap-4 text-[0.75rem]">
    <Stat label="Responses" value={q.count ?? 0} />
    <Stat label="Average" value={q.average ?? '—'} />
    <Stat label="Min" value={q.min ?? '—'} />
    <Stat label="Max" value={q.max ?? '—'} />
    {q.question_type === 'nps' && (
      <>
        <Stat label="NPS" value={q.nps_score ?? '—'} highlight />
        <Stat label="Promoters" value={q.breakdown?.promoters ?? 0} />
        <Stat label="Passives" value={q.breakdown?.passives ?? 0} />
        <Stat label="Detractors" value={q.breakdown?.detractors ?? 0} />
      </>
    )}
  </div>
);

const Stat: React.FC<{ label: string; value: React.ReactNode; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className={`px-3 py-2 rounded-lg ${highlight ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
    <div className={`text-[0.6rem] uppercase ${highlight ? 'text-gray-300' : 'text-gray-400'}`}>{label}</div>
    <div className="text-sm font-semibold tabular-nums">{value}</div>
  </div>
);

const TextResult: React.FC<{ q: SurveyQuestionResult }> = ({ q }) => (
  <div className="space-y-1.5 max-h-72 overflow-y-auto">
    {(q.answers || []).map((a, i) => (
      <div key={i} className="text-[0.72rem] text-gray-700 bg-gray-50 rounded px-2.5 py-1.5">
        {a.text || <span className="text-gray-400">(empty)</span>}
      </div>
    ))}
    {(q.answers || []).length === 0 && <p className="text-[0.72rem] text-gray-400">No answers yet.</p>}
  </div>
);

const SurveyResults: React.FC<Props> = ({ surveyId, onBack }) => {
  const [tab, setTab] = useState<'aggregate' | 'responses'>('aggregate');
  const [results, setResults] = useState<Results | null>(null);
  const [responses, setResponses] = useState<SurveyResponsesPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadResults = useCallback(async () => {
    try {
      setLoading(true);
      setResults(await getSurveyResults(surveyId));
    } catch (err: any) {
      toast.error(err.message || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [surveyId]);

  const loadResponses = useCallback(async (off: number) => {
    try {
      setResponses(await getSurveyResponses(surveyId, PAGE_SIZE, off));
    } catch (err: any) {
      toast.error(err.message || 'Failed to load responses');
    }
  }, [surveyId]);

  useEffect(() => { loadResults(); }, [loadResults]);
  useEffect(() => {
    if (tab === 'responses') loadResponses(offset);
  }, [tab, offset, loadResponses]);

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportSurveyCsv(surveyId, `survey_${surveyId}_responses.csv`);
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const total = results?.total_responses ?? 0;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <button onClick={onBack} className="flex items-center gap-1 text-[0.75rem] text-gray-500 hover:text-gray-800 mb-4">
        <ArrowLeft size={14} /> Back to surveys
      </button>

      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">{results?.survey.title || 'Survey results'}</h1>
          <p className="text-[0.75rem] text-gray-500 flex items-center gap-1.5">
            <Users size={13} /> {total} response{total === 1 ? '' : 's'}
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || total === 0}
          className="flex items-center gap-1.5 text-[0.75rem] px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          <Download size={14} /> {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-gray-100">
        {([['aggregate', 'Summary', BarChart3], ['responses', 'Responses', ListChecks]] as const).map(([key, label, Icon]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 text-[0.75rem] px-3 py-2 border-b-2 -mb-px ${
              tab === key ? 'border-black text-black font-medium' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[0.8rem] text-gray-500 py-10 text-center">Loading…</div>
      ) : tab === 'aggregate' ? (
        <div className="space-y-5">
          {(results?.results || []).map((q) => (
            <div key={q.question_id} className="border border-gray-100 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-[0.8rem] font-medium text-gray-900">{q.question_text}</h3>
                <span className="text-[0.6rem] uppercase text-gray-400">{q.question_type}</span>
              </div>
              {q.question_type === 'single' || q.question_type === 'multi'
                ? <ChoiceResult q={q} />
                : q.question_type === 'text'
                  ? <TextResult q={q} />
                  : <ScaleResult q={q} />}
            </div>
          ))}
          {(results?.results || []).length === 0 && (
            <div className="text-[0.8rem] text-gray-400 py-10 text-center">No questions.</div>
          )}
        </div>
      ) : (
        <ResponsesTable
          page={responses}
          offset={offset}
          onPage={(o) => setOffset(o)}
        />
      )}
    </div>
  );
};

const ResponsesTable: React.FC<{
  page: SurveyResponsesPage | null;
  offset: number;
  onPage: (offset: number) => void;
}> = ({ page, offset, onPage }) => {
  if (!page) return <div className="text-[0.8rem] text-gray-500 py-10 text-center">Loading…</div>;
  if (page.total === 0) return <div className="text-[0.8rem] text-gray-400 py-10 text-center">No responses yet.</div>;

  const fmtAnswer = (a: any) => Array.isArray(a) ? a.join(', ') : String(a);

  return (
    <div>
      <div className="overflow-x-auto border border-gray-100 rounded-lg">
        <table className="w-full text-[0.72rem]">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left font-medium px-3 py-2">Respondent</th>
              <th className="text-left font-medium px-3 py-2">Artist</th>
              <th className="text-left font-medium px-3 py-2">Submitted</th>
              <th className="text-left font-medium px-3 py-2">Answers</th>
            </tr>
          </thead>
          <tbody>
            {page.responses.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 align-top">
                <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                  {page.anonymous ? <span className="text-gray-400 italic">anonymous</span>
                    : (r.respondent_name?.trim() || `User #${r.user_id}`)}
                </td>
                <td className="px-3 py-2 text-gray-500">{page.anonymous ? '—' : (r.artist_id ?? '—')}</td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                  {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2 text-gray-700">
                  <div className="flex flex-wrap gap-1">
                    {r.answers.map((a, i) => (
                      <span key={i} className="bg-gray-100 rounded px-1.5 py-0.5">{fmtAnswer(a.answer)}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-[0.72rem] text-gray-500">
        <span>{offset + 1}–{Math.min(offset + page.limit, page.total)} of {page.total}</span>
        <div className="flex gap-2">
          <button
            disabled={offset === 0}
            onClick={() => onPage(Math.max(0, offset - page.limit))}
            className="px-2.5 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
          >
            Prev
          </button>
          <button
            disabled={offset + page.limit >= page.total}
            onClick={() => onPage(offset + page.limit)}
            className="px-2.5 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyResults;
